import { useEffect, useState, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import api, { setAuthToken } from "../utils/axiosInstance";

import ProductsManager from "./ProductsManager";
import DealsManager from "./DealsManager";

const SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes

const ShopDashboard = () => {
  const { getAccessTokenSilently, logout } = useAuth0();
  const navigate = useNavigate();

  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Fetch shop data
  const fetchShop = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      setAuthToken(token);

      const res = await api.get("/shops/my");
      setShop(res.data || null);
    } catch (err) {
      console.error("Failed to fetch shop:", err);
      setShop(null);
      setToast({
        open: true,
        message: "Failed to load shop",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchShop();
  }, [fetchShop]);

  // Session timeout logic
  useEffect(() => {
    let timeout;
    const resetTimeout = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        logout({ logoutParams: { returnTo: window.location.origin } });
      }, SESSION_TIMEOUT);
    };

    window.addEventListener("mousemove", resetTimeout);
    window.addEventListener("keydown", resetTimeout);

    resetTimeout(); // start timer

    return () => {
      clearTimeout(timeout);
      window.removeEventListener("mousemove", resetTimeout);
      window.removeEventListener("keydown", resetTimeout);
    };
  }, [logout]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!shop) {
    return (
      <Box
        p={3}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
      >
        <Typography variant="h6" gutterBottom>
          You don’t have a shop yet.
        </Typography>
        <Typography variant="body2" gutterBottom color="text.secondary">
          Create a shop to start adding products and managing your business.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
          onClick={() => navigate("/shops/my")}
        >
          Create Shop
        </Button>
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, sm: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" gutterBottom>
          {shop.name} — Products
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
        >
          Logout
        </Button>
      </Box>

      {/* Products for all sellers */}
      <ProductsManager shopSlug={shop.slug} />

      {/* Deals only for premium sellers */}
      {shop.is_premium && (
        <>
          <Typography variant="h6" mt={4} gutterBottom>
            Exclusive Deals
          </Typography>
          <DealsManager shopSlug={shop.slug} isPremium={true} />
        </>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
      >
        <Alert severity={toast.severity}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ShopDashboard;
